import { supabase } from '../lib/supabase';

export interface Transaction {
  id: string;
  date: string;
  amount: number;
  is_incompressible?: boolean;
  is_fixed_cost?: boolean;
  is_recurring?: boolean;
  is_internal_transfer?: boolean;
}

export interface SurplusRecommendation {
  category: 'securisation' | 'optimisation' | 'investissement' | 'redistribution';
  title: string;
  message: string;
  actions: string[];
}

export interface IndicatorsResult {
  opening_cash: number;
  opening_cash_from_accounts: number;
  opening_cash_difference: number;
  burn_rate_monthly: number;
  burn_rate_monthly_recurring: number;
  burn_rate_daily: number;
  tsf_days: number;
  tsf_months: number;
  tsf_days_recurring: number;
  tsf_months_recurring: number;
  min_cash_over_horizon: number;
  rupture_date: string | null;
  frd_required: number;
  frd_safety_days: number;
  safety_gap: number;
  avg_cash_in_monthly: number;
  recommended_cash_injection: number;
  recommended_monthly_cost_reduction: number;
  recommended_receivable_acceleration_days: number;
  surplus_state: 'deficit' | 'balanced' | 'moderate_surplus' | 'significant_surplus' | 'structural_surplus';
  surplus_amount: number;
  months_covered: number;
  security_months_configured: number;
  mobilizable_surplus: number;
  surplus_recommendations: SurplusRecommendation[];
  exceptional_expenses: Array<{
    date: string;
    amount: number;
    label: string;
  }>;
  calculation_details: Record<string, any>;
}

export interface StressTestOptions {
  start_date: Date;
  horizon_days: number;
  compressible_reduction_pct: number;
  buffer_pct: number;
  tsf_target_months: number;
  security_margin_months: number;
  exceptional_threshold: number;
  use_accounts_for_cash: boolean;
}

const DEFAULT_OPTIONS: StressTestOptions = {
  start_date: new Date(),
  horizon_days: 180,
  compressible_reduction_pct: 0,
  buffer_pct: 0.10,
  tsf_target_months: 3,
  security_margin_months: 0.75,
  exceptional_threshold: 10000,
  use_accounts_for_cash: true,
};

function analyzeSurplusState(
  opening_cash: number,
  frd_required: number,
  burn_rate_monthly: number,
  security_margin_months: number
): {
  state: 'deficit' | 'balanced' | 'moderate_surplus' | 'significant_surplus' | 'structural_surplus';
  surplus_amount: number;
  months_covered: number;
  mobilizable_surplus: number;
} {
  const safety_gap = opening_cash - frd_required;
  const security_buffer = burn_rate_monthly * security_margin_months;
  const total_required = frd_required + security_buffer;

  const surplus_amount = opening_cash - total_required;
  const months_covered = burn_rate_monthly > 0 ? opening_cash / burn_rate_monthly : 999999;
  const mobilizable_surplus = Math.max(0, surplus_amount);

  if (safety_gap < 0) {
    return {
      state: 'deficit',
      surplus_amount: safety_gap,
      months_covered,
      mobilizable_surplus: 0,
    };
  }

  if (surplus_amount < 0) {
    return {
      state: 'balanced',
      surplus_amount: 0,
      months_covered,
      mobilizable_surplus: 0,
    };
  }

  if (surplus_amount < burn_rate_monthly) {
    return {
      state: 'moderate_surplus',
      surplus_amount,
      months_covered,
      mobilizable_surplus,
    };
  }

  if (surplus_amount < burn_rate_monthly * 2) {
    return {
      state: 'significant_surplus',
      surplus_amount,
      months_covered,
      mobilizable_surplus,
    };
  }

  return {
    state: 'structural_surplus',
    surplus_amount,
    months_covered,
    mobilizable_surplus,
  };
}

function generateSurplusRecommendations(
  state: string,
  surplus_amount: number,
  months_covered: number,
  burn_rate_monthly: number
): SurplusRecommendation[] {
  const recommendations: SurplusRecommendation[] = [];

  if (state === 'moderate_surplus') {
    recommendations.push({
      category: 'securisation',
      title: 'Confortez votre sécurité',
      message: 'Votre trésorerie est saine mais encore proche du seuil de sécurité. Vous pouvez choisir de renforcer votre réserve pour absorber un aléa futur.',
      actions: [
        'Renforcer la réserve de sécurité sur 1 mois supplémentaire',
        'Identifier et réduire les dépendances critiques (client unique, échéance importante)',
        'Lisser certains décaissements pour étaler la charge de trésorerie',
      ],
    });
  }

  if (state === 'significant_surplus' || state === 'structural_surplus') {
    recommendations.push({
      category: 'optimisation',
      title: 'Optimisez votre trésorerie',
      message: `Une partie de votre trésorerie (${surplus_amount.toLocaleString('fr-FR', { maximumFractionDigits: 0 })}€) n'est pas mobilisée à court terme. Elle pourrait être optimisée sans impacter votre sécurité financière.`,
      actions: [
        'Envisager un remboursement anticipé de charges financières coûteuses',
        'Négocier de meilleures conditions bancaires grâce à votre position de trésorerie',
        'Constituer une réserve dédiée pour les investissements futurs',
      ],
    });
  }

  if (state === 'structural_surplus') {
    recommendations.push({
      category: 'investissement',
      title: 'Investissez de manière maîtrisée',
      message: `Votre entreprise génère durablement plus de trésorerie qu'elle n'en consomme (${months_covered.toFixed(1)} mois couverts). Vous disposez d'une capacité d'investissement sans mise en danger immédiate.`,
      actions: [
        'Réaliser un investissement productif ciblé (outil, équipement, formation)',
        "Renforcer l'équipe sur un poste stratégique",
        'Améliorer des outils ou processus qui génèrent de la valeur',
        'Tester un projet limité et réversible sans engager toute la trésorerie',
      ],
    });

    if (surplus_amount > burn_rate_monthly * 3) {
      recommendations.push({
        category: 'redistribution',
        title: 'Envisagez une redistribution réfléchie',
        message: "Votre trésorerie permet d'envisager une meilleure rémunération du risque ou une baisse de pression opérationnelle.",
        actions: [
          'Évaluer une rémunération exceptionnelle pour les porteurs de risque',
          'Augmenter progressivement les rémunérations tout en maintenant la sécurité',
          'Choisir assumément une marge de confort supérieure pour réduire le stress',
        ],
      });
    }
  }

  return recommendations;
}

export async function computeIndicators(
  userId: string,
  scenarioId: string | null = null,
  options: Partial<StressTestOptions> = {}
): Promise<IndicatorsResult> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  const opening_cash_result = await calculateOpeningCashReconciled(userId, opts.start_date, opts.use_accounts_for_cash);
  const opening_cash = opening_cash_result.reconciled;
  const opening_cash_from_accounts = opening_cash_result.from_accounts;
  const opening_cash_difference = opening_cash_result.difference;

  const transactions = await fetchTransactions(userId, scenarioId);

  const burn_rate_result = await calculateBurnRateWithExceptional(userId, opts.start_date, opts.exceptional_threshold);
  const burn_rate_monthly = burn_rate_result.total;
  const burn_rate_monthly_recurring = burn_rate_result.recurring;
  const exceptional_expenses = burn_rate_result.exceptional_expenses;

  const burn_rate_daily = burn_rate_monthly / 30.44;
  const burn_rate_daily_recurring = burn_rate_monthly_recurring / 30.44;

  let tsf_days = 0;
  let tsf_months = 0;
  let tsf_days_recurring = 0;
  let tsf_months_recurring = 0;

  if (burn_rate_daily > 0) {
    tsf_days = Math.floor(opening_cash / burn_rate_daily);
    tsf_months = tsf_days / 30.44;
  } else {
    tsf_days = 999999;
    tsf_months = 999999;
  }

  if (burn_rate_daily_recurring > 0) {
    tsf_days_recurring = Math.floor(opening_cash / burn_rate_daily_recurring);
    tsf_months_recurring = tsf_days_recurring / 30.44;
  } else {
    tsf_days_recurring = 999999;
    tsf_months_recurring = 999999;
  }

  const avg_cash_in_monthly = await calculateAvgCashIn(userId, opts.start_date);

  const stress_result = await runStressTestSimulation(
    opening_cash,
    transactions,
    opts
  );

  const { min_cash_over_horizon, rupture_date } = stress_result;

  const frd_result = await calculateFRD(userId, opening_cash, opts.start_date, opts.horizon_days);
  const frd_required = frd_result.frd_amount;

  const safety_gap = opening_cash - frd_required;

  let recommended_cash_injection = 0;
  let recommended_monthly_cost_reduction = 0;
  let recommended_receivable_acceleration_days = 0;

  if (safety_gap < 0) {
    recommended_cash_injection = Math.abs(safety_gap);
    recommended_monthly_cost_reduction = Math.abs(safety_gap) / opts.tsf_target_months;

    if (avg_cash_in_monthly > 0) {
      const acceleration_days = Math.abs(safety_gap) / (avg_cash_in_monthly / 30.44);
      recommended_receivable_acceleration_days = Math.ceil(acceleration_days);
    }
  }

  const surplusAnalysis = analyzeSurplusState(
    opening_cash,
    frd_required,
    burn_rate_monthly,
    opts.security_margin_months
  );

  const surplus_recommendations = generateSurplusRecommendations(
    surplusAnalysis.state,
    surplusAnalysis.surplus_amount,
    surplusAnalysis.months_covered,
    burn_rate_monthly
  );

  return {
    opening_cash,
    opening_cash_from_accounts,
    opening_cash_difference,
    burn_rate_monthly,
    burn_rate_monthly_recurring,
    burn_rate_daily,
    tsf_days,
    tsf_months,
    tsf_days_recurring,
    tsf_months_recurring,
    min_cash_over_horizon,
    rupture_date: rupture_date ? rupture_date.toISOString().split('T')[0] : null,
    frd_required,
    frd_safety_days: frd_result.safety_days,
    safety_gap,
    avg_cash_in_monthly,
    recommended_cash_injection,
    recommended_monthly_cost_reduction,
    recommended_receivable_acceleration_days,
    surplus_state: surplusAnalysis.state,
    surplus_amount: surplusAnalysis.surplus_amount,
    months_covered: surplusAnalysis.months_covered,
    security_months_configured: opts.security_margin_months,
    mobilizable_surplus: surplusAnalysis.mobilizable_surplus,
    surplus_recommendations,
    exceptional_expenses,
    calculation_details: {
      options: opts,
      burn_rate_monthly,
      burn_rate_monthly_recurring,
      avg_cash_in_monthly,
      frd_details: frd_result,
      surplus_analysis: surplusAnalysis,
      opening_cash_reconciliation: opening_cash_result,
    },
  };
}

async function calculateOpeningCash(userId: string, asOfDate: Date): Promise<number> {
  const { data: transactions } = await supabase
    .from('transactions')
    .select('amount, is_internal_transfer')
    .eq('user_id', userId)
    .lte('date', asOfDate.toISOString().split('T')[0]);

  if (!transactions) return 0;

  return transactions
    .filter(t => !t.is_internal_transfer)
    .reduce((sum, t) => sum + Number(t.amount), 0);
}

async function calculateOpeningCashReconciled(
  userId: string,
  asOfDate: Date,
  useAccounts: boolean = true
): Promise<{
  from_transactions: number;
  from_accounts: number;
  reconciled: number;
  difference: number;
}> {
  const { data: transactions } = await supabase
    .from('transactions')
    .select('amount, is_internal_transfer')
    .eq('user_id', userId)
    .lte('date', asOfDate.toISOString().split('T')[0]);

  const from_transactions = transactions
    ? transactions
        .filter(t => !t.is_internal_transfer)
        .reduce((sum, t) => sum + Number(t.amount), 0)
    : 0;

  const { data: accounts } = await supabase
    .from('treasury_accounts')
    .select('current_balance')
    .eq('user_id', userId)
    .eq('is_active', true);

  const from_accounts = accounts
    ? accounts.reduce((sum, a) => sum + Number(a.current_balance), 0)
    : 0;

  const difference = from_accounts - from_transactions;

  const reconciled = useAccounts && from_accounts > 0
    ? from_accounts
    : from_transactions;

  return {
    from_transactions,
    from_accounts,
    reconciled,
    difference,
  };
}

async function fetchTransactions(userId: string, scenarioId: string | null): Promise<Transaction[]> {
  const query = supabase
    .from('transactions')
    .select('*')
    .eq('user_id', userId)
    .order('date', { ascending: true });

  const { data } = await query;

  if (!data) return [];

  if (scenarioId) {
    const { data: assumptions } = await supabase
      .from('scenario_assumptions')
      .select('*')
      .eq('scenario_id', scenarioId);

    return applyScenarioAssumptions(data, assumptions || []);
  }

  return data;
}

function applyScenarioAssumptions(transactions: any[], assumptions: any[]): Transaction[] {
  let adjusted = [...transactions];

  for (const assumption of assumptions) {
    if (assumption.assumption_type === 'delay_in_days' && assumption.target_type === 'category') {
      adjusted = adjusted.map(t => {
        if (t.category_id === assumption.target_id) {
          const newDate = new Date(t.date);
          newDate.setDate(newDate.getDate() + (assumption.value_number || 0));
          return { ...t, date: newDate.toISOString().split('T')[0] };
        }
        return t;
      });
    } else if (assumption.assumption_type === 'change_amount' && assumption.target_type === 'category') {
      adjusted = adjusted.map(t => {
        if (t.category_id === assumption.target_id) {
          return { ...t, amount: Number(t.amount) * (1 + (assumption.value_number || 0) / 100) };
        }
        return t;
      });
    }
  }

  return adjusted;
}

async function calculateBurnRate(userId: string, asOfDate: Date): Promise<number> {
  const threeMonthsAgo = new Date(asOfDate);
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

  const { data: transactions } = await supabase
    .from('transactions')
    .select('amount, date, is_internal_transfer')
    .eq('user_id', userId)
    .gte('date', threeMonthsAgo.toISOString().split('T')[0])
    .lte('date', asOfDate.toISOString().split('T')[0])
    .lt('amount', 0);

  if (!transactions || transactions.length === 0) return 0;

  const expenses = transactions.filter(t => !t.is_internal_transfer);
  if (expenses.length === 0) return 0;

  const totalExpenses = expenses.reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0);
  const monthsCount = Math.max(1, (asOfDate.getTime() - threeMonthsAgo.getTime()) / (1000 * 60 * 60 * 24 * 30.44));

  return totalExpenses / monthsCount;
}

async function calculateBurnRateWithExceptional(
  userId: string,
  asOfDate: Date,
  exceptionalThreshold: number = 10000
): Promise<{
  total: number;
  recurring: number;
  exceptional_expenses: Array<{ date: string; amount: number; label: string }>;
}> {
  const threeMonthsAgo = new Date(asOfDate);
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

  const { data: transactions } = await supabase
    .from('transactions')
    .select('amount, date, label, is_internal_transfer')
    .eq('user_id', userId)
    .gte('date', threeMonthsAgo.toISOString().split('T')[0])
    .lte('date', asOfDate.toISOString().split('T')[0])
    .lt('amount', 0);

  if (!transactions || transactions.length === 0) {
    return {
      total: 0,
      recurring: 0,
      exceptional_expenses: [],
    };
  }

  const expenses = transactions.filter(t => !t.is_internal_transfer);
  if (expenses.length === 0) {
    return {
      total: 0,
      recurring: 0,
      exceptional_expenses: [],
    };
  }

  const exceptional_expenses: Array<{ date: string; amount: number; label: string }> = [];
  let totalExpenses = 0;
  let recurringExpenses = 0;

  for (const expense of expenses) {
    const amount = Math.abs(Number(expense.amount));
    totalExpenses += amount;

    if (amount >= exceptionalThreshold) {
      exceptional_expenses.push({
        date: expense.date,
        amount: -amount,
        label: expense.label || 'Sans libellé',
      });
    } else {
      recurringExpenses += amount;
    }
  }

  const monthsCount = Math.max(
    1,
    (asOfDate.getTime() - threeMonthsAgo.getTime()) / (1000 * 60 * 60 * 24 * 30.44)
  );

  return {
    total: totalExpenses / monthsCount,
    recurring: recurringExpenses / monthsCount,
    exceptional_expenses: exceptional_expenses.sort((a, b) => a.amount - b.amount),
  };
}

async function calculateFRD(
  userId: string,
  opening_cash: number,
  asOfDate: Date,
  horizon_days: number
): Promise<{ frd_amount: number; min_cash_reached: number; safety_days: number }> {
  const startDate = new Date(asOfDate);
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + horizon_days);

  const { data: forecastLines } = await supabase
    .from('cash_flow_forecast_lines')
    .select('amount, forecast_date')
    .eq('user_id', userId)
    .gte('forecast_date', startDate.toISOString().split('T')[0])
    .lte('forecast_date', endDate.toISOString().split('T')[0]);

  const { data: futureTransactions } = await supabase
    .from('transactions')
    .select('amount, date, is_internal_transfer')
    .eq('user_id', userId)
    .gte('date', startDate.toISOString().split('T')[0])
    .lte('date', endDate.toISOString().split('T')[0]);

  let dailyCashflows: Map<string, number> = new Map();

  if (forecastLines && forecastLines.length > 0) {
    for (const line of forecastLines) {
      const dateKey = line.forecast_date;
      const current = dailyCashflows.get(dateKey) || 0;
      dailyCashflows.set(dateKey, current + Number(line.amount));
    }
  }

  if (futureTransactions && futureTransactions.length > 0) {
    for (const tx of futureTransactions) {
      if (!tx.is_internal_transfer) {
        const dateKey = tx.date;
        const current = dailyCashflows.get(dateKey) || 0;
        dailyCashflows.set(dateKey, current + Number(tx.amount));
      }
    }
  }

  let cash = opening_cash;
  let min_cash = opening_cash;
  let rupture_day = -1;

  for (let d = 0; d < horizon_days; d++) {
    const currentDate = new Date(startDate);
    currentDate.setDate(currentDate.getDate() + d);
    const dateKey = currentDate.toISOString().split('T')[0];

    const dailyFlow = dailyCashflows.get(dateKey) || 0;
    cash += dailyFlow;

    if (cash < min_cash) {
      min_cash = cash;
    }

    if (cash <= 0 && rupture_day === -1) {
      rupture_day = d;
    }
  }

  let frd_amount = 0;
  if (min_cash < 0) {
    frd_amount = Math.abs(min_cash);
  }

  const safety_days = rupture_day === -1 ? horizon_days : rupture_day;

  return {
    frd_amount,
    min_cash_reached: min_cash,
    safety_days,
  };
}

async function calculateAvgCashIn(userId: string, asOfDate: Date): Promise<number> {
  const threeMonthsAgo = new Date(asOfDate);
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

  const { data: transactions } = await supabase
    .from('transactions')
    .select('amount, date, is_internal_transfer')
    .eq('user_id', userId)
    .gte('date', threeMonthsAgo.toISOString().split('T')[0])
    .lte('date', asOfDate.toISOString().split('T')[0])
    .gt('amount', 0);

  if (!transactions || transactions.length === 0) return 0;

  const inflows = transactions.filter(t => !t.is_internal_transfer);
  if (inflows.length === 0) return 0;

  const totalInflows = inflows.reduce((sum, t) => sum + Number(t.amount), 0);
  const monthsCount = Math.max(1, (asOfDate.getTime() - threeMonthsAgo.getTime()) / (1000 * 60 * 60 * 24 * 30.44));

  return totalInflows / monthsCount;
}

async function runStressTestSimulation(
  opening_cash: number,
  transactions: Transaction[],
  options: StressTestOptions
): Promise<{ min_cash_over_horizon: number; rupture_date: Date | null }> {
  let cash = opening_cash;
  let min_cash = opening_cash;
  let rupture_date: Date | null = null;

  const startDate = new Date(options.start_date);
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + options.horizon_days);

  const futureTx = transactions.filter(t => {
    const txDate = new Date(t.date);
    return txDate >= startDate && txDate <= endDate;
  });

  const hasIncompressibleData = futureTx.some(t => t.is_incompressible);

  const dailyCashflows: Map<string, number> = new Map();

  for (const tx of futureTx) {
    const dateKey = tx.date;
    const current = dailyCashflows.get(dateKey) || 0;

    if (Number(tx.amount) > 0) {
      continue;
    }

    if (hasIncompressibleData) {
      if (tx.is_incompressible) {
        dailyCashflows.set(dateKey, current + Number(tx.amount));
      } else {
        const adjusted = Number(tx.amount) * (1 - options.compressible_reduction_pct / 100);
        dailyCashflows.set(dateKey, current + adjusted);
      }
    } else {
      dailyCashflows.set(dateKey, current + Number(tx.amount));
    }
  }

  for (let d = 0; d < options.horizon_days; d++) {
    const currentDate = new Date(startDate);
    currentDate.setDate(currentDate.getDate() + d);
    const dateKey = currentDate.toISOString().split('T')[0];

    const dailyFlow = dailyCashflows.get(dateKey) || 0;
    cash += dailyFlow;

    if (cash < min_cash) {
      min_cash = cash;
    }

    if (cash <= 0 && rupture_date === null) {
      rupture_date = currentDate;
    }
  }

  return { min_cash_over_horizon: min_cash, rupture_date };
}

export async function saveIndicatorsSnapshot(
  userId: string,
  indicators: IndicatorsResult,
  scenarioId: string | null = null,
  stressTestId: string | null = null
): Promise<string> {
  const { data, error } = await supabase
    .from('indicators_snapshots')
    .insert({
      user_id: userId,
      scenario_id: scenarioId,
      stress_test_id: stressTestId,
      as_of_date: new Date().toISOString().split('T')[0],
      ...indicators,
    })
    .select('id')
    .single();

  if (error) throw error;
  return data.id;
}
